import React from 'react';

interface CommunityChallengeProps {
    challenge: {
        id: number;
        name: string;
        // picture: string; // Replaced by community_reward_pictures
        description: string;
        // total_points_required: number; // Replaced by target_value
        target_value: number; 
        progress_percentage: number;
        is_unlocked: boolean;
        community_reward_pictures: { id: number; picture: string }[]; 
    };
}

const CommunityChallenge: React.FC<CommunityChallengeProps> = ({ challenge }) => {
    // Calculate current_points from progress_percentage and target_value
    const currentPoints = Math.round((challenge.progress_percentage / 100) * challenge.target_value);
    // Consider a challenge complete if it's unlocked OR if progress is 100%
    const isComplete = challenge.is_unlocked || challenge.progress_percentage >= 100;

    // State for current image index and full-screen view
    const [currentImageIndex, setCurrentImageIndex] = React.useState(0);
    const [isFullScreen, setIsFullScreen] = React.useState(false);
    const [fullScreenImage, setFullScreenImage] = React.useState<string | null>(null);
    const [showThumbnails, setShowThumbnails] = React.useState(false);

    // Get the current picture URL or a default
    const pictures = challenge.community_reward_pictures;
    const pictureUrl = pictures && pictures.length > 0
        ? pictures[currentImageIndex]?.picture // Use optional chaining in case index is out of bounds temporarily
        : '/default-challenge.png';

    const handleNextImage = (event: React.MouseEvent) => {
        event.stopPropagation(); 
        if (pictures && pictures.length > 0) {
            setCurrentImageIndex((prevIndex) => (prevIndex + 1) % pictures.length);
        }
    };

    const toggleThumbnails = (event: React.MouseEvent) => {
        event.stopPropagation();
        setShowThumbnails(!showThumbnails);
    };

    const openFullScreen = (imageUrl: string) => {
        setFullScreenImage(imageUrl);
        setIsFullScreen(true);
    };

    const closeFullScreen = () => {
        setIsFullScreen(false);
        setFullScreenImage(null);
    };

    return (
        <>
            <div
                className={`relative rounded-lg p-4 mb-4 shadow-lg transition-all duration-300 ${isComplete
                    ? 'bg-gradient-to-r from-secondary-gold/40 to-secondary-gold/30 border-2 border-secondary-gold'
                    : 'bg-gradient-to-r from-primary-blue-80 to-primary-blue-90 border border-primary-blue-70'
                }`}
            >
                {/* Completion badge for completed challenges */}
                {isComplete && (
                    <div className="absolute -top-3 -right-3 bg-secondary-gold text-black font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 transform">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>Complete!</span>
                    </div>
                )}

                <div className="flex items-start gap-4">
                    {/* Challenge Image with different styling based on completion */}
                    <div className="flex-shrink-0 relative flex flex-col items-center">
                        <div className="relative">
                            <div className={`rounded-lg p-1 ${isComplete ? 'bg-secondary-gold/20' : 'bg-primary-blue-70/50'}`}>
                                <img
                                    src={pictureUrl}
                                    alt={challenge.name}
                                    className={`w-16 h-16 rounded-lg object-cover cursor-pointer ${isComplete ? 'border-2 border-secondary-gold' : ''
                                        }`}
                                    onClick={() => openFullScreen(pictureUrl)}
                                />
                            </div>
                            {isComplete && (
                                <div className="absolute -bottom-2 -right-2 bg-secondary-gold rounded-full p-1 shadow-lg">
                                    <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                </div>
                            )}
                        </div>

                        {pictures && pictures.length > 1 && (
                            <div className="mt-1.5 h-6 flex justify-center items-center w-full">
                                <div className="flex items-center space-x-1">
                                        {pictures.map((pic, index) => (
                                            <img
                                                key={pic.id}
                                                src={pic.picture}
                                                alt={`Thumbnail ${index + 1}`}
                                                className={`w-5 h-5 rounded object-cover cursor-pointer border-2 ${currentImageIndex === index
                                                    ? (isComplete ? 'border-secondary-gold' : 'border-blue-500')
                                                    : 'border-transparent hover:border-gray-400'
                                                    }`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCurrentImageIndex(index);
                                                }}
                                                title={`View image ${index + 1}`}
                                            />
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Challenge Details */}
                    <div className="flex-grow">
                        {/* Name with Icon */}
                        <div className="flex items-center gap-2 mb-1">
                            <svg
                                className={`w-5 h-5 ${isComplete ? 'text-secondary-gold' : 'text-secondary-gray'}`}
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M10 2a8 8 0 100 16 8 8 0 000-16zm0 14a6 6 0 110-12 6 6 0 010 12zm-1-9h2v4H9V7zm0 6h2v2H9v-2z" />
                            </svg>
                            <h3 className={`text-xl font-bold ${isComplete ? 'text-secondary-gold' : 'text-white'}`}>
                                {challenge.name}
                            </h3>
                        </div>

                        {/* Description */}
                        <p className={`text-sm mb-3 ${isComplete ? 'text-secondary-gold/80' : 'text-gray-light'}`}>
                            {challenge.description}
                        </p>

                        {/* Progress Bar with Icons */}
                        <div className="flex items-center justify-between">
                            <div className="flex-1 mr-2 flex items-center gap-2">
                                {/* Progress Bar - Different styles for complete vs in progress */}
                                <div className="h-4 bg-primary-blue-70/80 rounded-full flex-1 overflow-hidden shadow-inner border border-primary-blue-60">
                                    <div
                                        className={`h-full rounded-full transition-all duration-500 ease-in-out flex items-center justify-end pr-1 ${isComplete
                                            ? 'bg-gradient-to-r from-secondary-gold/80 to-secondary-gold'
                                            : 'bg-gradient-to-r from-secondary-gray/70 to-secondary-gray/90'
                                            }`}
                                        style={{ width: `${Math.min(challenge.progress_percentage, 100)}%` }}
                                    >
                                        {challenge.progress_percentage >= 30 &&
                                            <span className="text-xs font-bold text-black">
                                                {challenge.progress_percentage}%
                                            </span>
                                        }
                                    </div>
                                </div>

                                {/* Point Display */}
                                <span
                                    className={`text-sm font-bold px-2 py-1 rounded-lg ${isComplete
                                        ? 'bg-secondary-gold/20 text-secondary-gold border border-secondary-gold/40'
                                        : 'bg-primary-blue-70 text-gray-light border border-primary-blue-60'
                                        }`}
                                >
                                    {currentPoints}/{challenge.target_value}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isFullScreen && fullScreenImage && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                    onClick={closeFullScreen} // Close when clicking on the backdrop
                >
                    <img
                        src={fullScreenImage}
                        alt="Full screen challenge"
                        className="max-w-full max-h-full rounded-lg object-contain cursor-default"
                        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on the image itself
                    />
                    <button
                        className="absolute top-4 right-4 text-white text-2xl font-bold bg-black bg-opacity-30 hover:bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center"
                        onClick={closeFullScreen}
                        aria-label="Close full screen view"
                    >
                        &times;
                    </button>
                </div>
            )}
        </>
    );
};

export default CommunityChallenge; 